import {
  api,
  AuthExpiredError,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  isApiError,
  setTokens,
} from './api';
import { hasCode } from './api-error';
import { ROLES, type Role, type SessionUser } from './permissions';
import { clearUser, setUser } from './session';

export type { SessionUser };

interface TokenResponse {
  accessToken: string;
  refreshToken: string;
  user: { id: string; name: string; email: string; role: string };
}

interface MeResponse {
  id: string;
  name: string;
  email: string;
  role: string;
  language: string;
  twoFactorEnabled: boolean;
  permissions: string[] | null;
  areas: string[];
}

export type LoginOutcome = { status: 'ok'; user: SessionUser } | { status: 'two-factor' };

/**
 * Panele girebilecek roller. Sunucu kimliği doğrular ama "bu kişi PANELE
 * girebilir mi" ayrı bir sorudur; ileride panel dışı bir rol eklenirse
 * (örn. yalnız API kullanan servis hesabı) kapı burada kapanır.
 *
 * Giriş ve 2FA yollarının ORTAK kapısı olması önemli: ikisinden birinde
 * kontrol unutulursa 2FA'lı kullanıcı kontrolsüz girerdi.
 */
function assertPanelAccess(role: string): asserts role is Role {
  if (!(ROLES as readonly string[]).includes(role)) {
    clearTokens();
    clearUser();
    throw new Error('PANEL_ACCESS_DENIED');
  }
}

/** Token'lar yazıldıktan sonra sunucudan tam profili çeker ve yerele yazar. */
async function loadSession(): Promise<SessionUser> {
  const me = await api.get<MeResponse>('/auth/me');
  assertPanelAccess(me.role);
  const user: SessionUser = {
    id: me.id,
    name: me.name,
    email: me.email,
    role: me.role,
    language: me.language,
    twoFactorEnabled: me.twoFactorEnabled,
    permissions: me.permissions,
    areas: me.areas,
  };
  setUser(user);
  return user;
}

export async function login(email: string, password: string): Promise<LoginOutcome> {
  try {
    const res = await api.post<TokenResponse>(
      '/auth/login',
      { email, password },
      { anonymous: true },
    );
    assertPanelAccess(res.user.role);
    setTokens(res.accessToken, res.refreshToken);
    return { status: 'ok', user: await loadSession() };
  } catch (err) {
    // 2FA açık: şifre DOĞRU ama ikinci adım gerekiyor. Hata değil, akışın parçası.
    if (hasCode(err, 'TWO_FACTOR_REQUIRED')) return { status: 'two-factor' };
    throw err;
  }
}

/**
 * İkinci adım. Şifre BURADA DA gönderilir ve sunucu yeniden doğrular —
 * aksi hâlde geçerli bir TOTP kodu tek başına giriş anlamına gelirdi.
 */
export async function loginTwoFactor(
  email: string,
  password: string,
  code: string,
): Promise<SessionUser> {
  const res = await api.post<TokenResponse>(
    '/auth/login/2fa',
    { email, password, code },
    { anonymous: true },
  );
  assertPanelAccess(res.user.role);
  setTokens(res.accessToken, res.refreshToken);
  return loadSession();
}

/**
 * Çıkış. Refresh token da gönderilir — sunucu onu da iptal etmezse "çıkış yap"
 * oturumu bitirmez: elde kalan refresh token'la 7 gün boyunca yeni access
 * token üretilebilir.
 *
 * Sunucu isteği başarısız olsa bile yerel oturum HER HÂLÜKÂRDA temizlenir:
 * operatör "çıktım" dedikten sonra ekranda oturum açık kalmamalı.
 */
export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();
  try {
    if (getAccessToken()) {
      await api.post('/auth/logout', refreshToken ? { refreshToken } : {}, { noRefresh: true });
    }
  } catch {
    // Yutulur: ağ yoksa veya token zaten geçersizse de çıkış yapılmış sayılır.
  } finally {
    clearTokens();
    clearUser();
  }
}

/**
 * Sayfa açılışında sunucudan tazeleme.
 *
 * Oturum YALNIZCA kimlik/yetki hatasında düşürülür. Ağ kopukluğunda düşürmek,
 * geçici bir bağlantı sorununda operatörü şifresini yeniden girmeye zorlardı.
 */
export async function refreshStoredUser(): Promise<SessionUser | null> {
  if (!getAccessToken()) return null;
  try {
    return await loadSession();
  } catch (err) {
    const authFailure =
      err instanceof AuthExpiredError ||
      (isApiError(err) && (err.status === 401 || err.status === 403));
    if (authFailure) {
      clearTokens();
      clearUser();
    }
    return null;
  }
}
